import React, {useState, useRef, useEffect} from 'react';
import {ButtonBox, BUTTON_BOX_SIZE, IcClose, IcQMark, IcTxtArrowSmallBold, InputLineAmount} from '@defign/ui';
import className from 'classnames/bind';
import {useClickAway} from 'react-use';
import {formatMoneyBasedOnWon, MONEY_FORMAT_TYPE} from '@Root/utils/moneyFormatter';
import style from './MyhouseMainCalculator.module.scss';
import {CALCULATOR_MAX_VALUE, MAIN_CALCULATOR_ID} from '@Root/contants/myhouse';
import MortgagePartnerProduct from '@Root/types/inner/myhouse/MortgagePartnerProduct';
import {isEmpty} from 'lodash';
import {getStorage} from '@Root/utils/storage';
import {useParams} from 'react-router';

const cx = className.bind(style);

type TProps = {
    product: MortgagePartnerProduct | null;
};

const CALCULATOR_VALUE_KEY = 'CALCULATOR_VALUE';

const localStorage = getStorage('local');

const MyhouseMainCalculator = ({product}: TProps) => {
    const {houseNo} = useParams<{houseNo: string}>();

    const [value, setValue] = useState<string>(localStorage.get(CALCULATOR_VALUE_KEY)?.[houseNo]?.toString() ?? '');

    console.log(value);

    const [isShowTooltip, setIsShowTooltip] = useState(false);

    const tooltipRef = useRef<HTMLSpanElement>(null);

    useClickAway(tooltipRef, () => {
        setIsShowTooltip(false);
    });

    const handleInputChange = (next: {value: string; formattedValue: string}) => {
        if (next.value === '' || parseInt(next.value) < CALCULATOR_MAX_VALUE) {
            setValue(next.value);
            localStorage.set(CALCULATOR_VALUE_KEY, {
                ...localStorage.get(CALCULATOR_VALUE_KEY),
                [houseNo]: value,
            });
        }
    };

    useEffect(() => {
        setValue(localStorage.get(CALCULATOR_VALUE_KEY)?.[houseNo]?.toString() ?? '');
    }, [houseNo]);

    const aggregation = product?.aggregation;

    if (isEmpty(aggregation) || !aggregation) {
        return null;
    }

    const interest = (function () {
        if (!value) {
            return '???원';
        }

        const money = Math.ceil(parseInt(value) * (parseFloat(aggregation.minimumInterestRate) / 100) * (1 / 12));

        if (money < 1) {
            return '0원';
        }

        return formatMoneyBasedOnWon(money, MONEY_FORMAT_TYPE.TAX);
    })();

    return (
        <div className={cx('article')} id={MAIN_CALCULATOR_ID}>
            <div className={cx('area-title')}>
                <h2 className="blind">전월세대출</h2>
                <strong className={cx('title')}>
                    전월세대출이 있으신가요?
                    <br />
                    <em className={cx('highlight')}>이자가 얼마나 낮아질지</em> 알아보세요
                    <span ref={tooltipRef}>
                        <button
                            type="button"
                            className={cx('button-tooltip')}
                            onClick={() => setIsShowTooltip(!isShowTooltip)}
                        >
                            <IcQMark />
                            <span className="blind">툴팁 보기</span>
                        </button>
                        {isShowTooltip && (
                            <div className={cx('tooltip')}>
                                예상 월 원리금은 네이버페이 전월세대출 비교 제휴 금융회사의 금일 최저 금리를 기준으로
                                계산한 값이에요. 실제 심사 결과에 따라 납입 원리금이 달라질 수 있어요.
                                <button
                                    type="button"
                                    className={cx('button-close')}
                                    onClick={() => setIsShowTooltip(false)}
                                >
                                    <IcClose width={20} height={20} fill="#929294" />
                                    <span className="blind">닫기</span>
                                </button>
                            </div>
                        )}
                    </span>
                </strong>
            </div>
            <div className={cx('area-info')}>
                <label htmlFor="amount" className={cx('label')}>
                    내 대출 잔액
                </label>
                <InputLineAmount
                    value={value}
                    onChange={handleInputChange}
                    onClickDeleteButton={() => setValue('')}
                    placeholder="대출 잔액을 입력해 주세요"
                    unit=""
                    guideMessage={value && formatMoneyBasedOnWon(parseInt(value), MONEY_FORMAT_TYPE.TAX)}
                />
            </div>
            <dl className={cx('list')}>
                <div className={cx('item')}>
                    <dt className={cx('list-title')}>최저 금리</dt>
                    <dd className={cx('list-text')}>
                        <span className={cx('icon')}>
                            <img src={aggregation.partnerImageUrl} />
                        </span>
                        {aggregation.partnerName}
                        <em className={cx('percent')}>{aggregation.minimumInterestRate + '%'}</em>
                    </dd>
                </div>
                <div className={cx('item')}>
                    <dt className={cx('list-title')}>예상 월 이자</dt>
                    <dd className={cx('list-text')}>{interest}</dd>
                </div>
            </dl>
            <div className={cx('area-button')}>
                <ButtonBox
                    href={process.env.REACT_APP_LOAN_URL + '/jeonse'}
                    size={BUTTON_BOX_SIZE.LARGE_FULL}
                    text={
                        <span className={cx('button-inner')}>
                            <span className={cx('button-text')}>최저 금리로 갈아탈 수 있을지 알아보기</span>
                            <span className={cx('icon-arrow')}>
                                <IcTxtArrowSmallBold width={6} height={10} fill="#09aa5c" />
                            </span>
                        </span>
                    }
                    color="green"
                    variant="secondary"
                />
            </div>
        </div>
    );
};

export default MyhouseMainCalculator;
